import { useState, useEffect, useMemo } from 'react';
import { INITIAL_FORM_DATA } from '../components/FalconServiceDashboard/copyConfigFormConfig';

/**
 * Custom hook to manage copy configuration form state and cascading logic
 */
export const useCopyConfigForm = ({
  clientHierarchy,
  availableClients,
  allClients,
  fromSidebar
}) => {
  // --- Local State (always include all fields) ---
  const [formData, setFormData] = useState({
    ...INITIAL_FORM_DATA,
    destinationClient: '',
    destinationSubClient: '',
    destinationCurrency: ''
  });

  // --- Destination Clients (exclude selected source) ---
  const destinationClients = useMemo(() => {
    if (fromSidebar) return [];
    return allClients.filter((clientName) => clientName !== formData.sourceClient);
  }, [fromSidebar, allClients, formData.sourceClient]);

  // --- Source Dropdowns ---
  const sourceSubClients = useMemo(() => {
    if (!formData.sourceClient || !clientHierarchy[formData.sourceClient])
      return [];
    return [...Object.keys(clientHierarchy[formData.sourceClient])].sort();
  }, [formData.sourceClient, clientHierarchy]);

  const sourceCurrencies = useMemo(() => {
    if (
      !formData.sourceClient ||
      !formData.sourceSubClient ||
      !clientHierarchy[formData.sourceClient]?.[formData.sourceSubClient]
    ) {
      return [];
    }
    return [...clientHierarchy[formData.sourceClient][formData.sourceSubClient]].sort();
  }, [formData.sourceClient, formData.sourceSubClient, clientHierarchy]);

  // --- Destination Dropdowns ---
  const destinationSubClients = useMemo(() => {
    if (fromSidebar || !formData.destinationClient || !clientHierarchy[formData.destinationClient])
      return [];
    return [...Object.keys(clientHierarchy[formData.destinationClient])].sort();
  }, [fromSidebar, formData.destinationClient, clientHierarchy]);

  const destinationCurrencies = useMemo(() => {
    if (
      fromSidebar ||
      !formData.destinationClient ||
      !formData.destinationSubClient ||
      !clientHierarchy[formData.destinationClient]?.[formData.destinationSubClient]
    ) {
      return [];
    }
    return [...clientHierarchy[formData.destinationClient][formData.destinationSubClient]].sort();
  }, [fromSidebar, formData.destinationClient, formData.destinationSubClient, clientHierarchy]);

  // --- Initialize Form ---
  useEffect(() => {
    if (availableClients.length === 0 || formData.sourceClient) return;

    // Initialize source
    const firstClient = availableClients[0];
    const subClients = [...Object.keys(clientHierarchy[firstClient] || {})].sort();
    const firstSubClient = subClients[0] || 'DEFAULT';
    const currencies = [...(clientHierarchy[firstClient]?.[firstSubClient] || [])].sort();
    const firstCurrency = currencies[0] || '';

    const updates = {
      sourceClient: firstClient,
      sourceSubClient: firstSubClient,
      sourceCurrency: firstCurrency,
    };

    // Initialize destination (only for dashboard mode)
    if (!fromSidebar && allClients.length > 1) {
      const destClient = allClients.find(c => c !== firstClient) || allClients[0];
      const destSubClients = [...Object.keys(clientHierarchy[destClient] || {})].sort();
      const destFirstSubClient = destSubClients[0] || 'DEFAULT';
      const destCurrencies = [...(clientHierarchy[destClient]?.[destFirstSubClient] || [])].sort();
      const destFirstCurrency = destCurrencies[0] || '';

      updates.destinationClient = destClient;
      updates.destinationSubClient = destFirstSubClient;
      updates.destinationCurrency = destFirstCurrency;
    }

    setFormData((prev) => ({ ...prev, ...updates }));
  }, [availableClients, formData.sourceClient, clientHierarchy, fromSidebar, allClients]);

  // --- Handle Cascading Changes ---
  const handleChange = ({ target: { name, value, type, checked } }) => {
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };

      // Source client change
      if (name === 'sourceClient') {
        const subClients = [...Object.keys(clientHierarchy[value] || {})].sort();
        const firstSubClient = subClients[0] || 'DEFAULT';
        const currencies = [...(clientHierarchy[value]?.[firstSubClient] || [])].sort();
        const firstCurrency = currencies[0] || '';

        updated.sourceSubClient = firstSubClient;
        updated.sourceCurrency = firstCurrency;

        // If destination is same as source, change it
        if (!fromSidebar && prev.destinationClient === value) {
          const availableDest = allClients.filter(c => c !== value);
          if (availableDest.length > 0) {
            const newDestClient = availableDest[0];
            const destSubClients = [...Object.keys(clientHierarchy[newDestClient] || {})].sort();
            const destFirstSubClient = destSubClients[0] || 'DEFAULT';
            const destCurrencies = [...(clientHierarchy[newDestClient]?.[destFirstSubClient] || [])].sort();
            const destFirstCurrency = destCurrencies[0] || '';

            updated.destinationClient = newDestClient;
            updated.destinationSubClient = destFirstSubClient;
            updated.destinationCurrency = destFirstCurrency;
          }
        }
      }

      // Source subclient change
      if (name === 'sourceSubClient') {
        const currencies = [...(clientHierarchy[prev.sourceClient]?.[value] || [])].sort();
        const firstCurrency = currencies[0] || '';
        updated.sourceCurrency = firstCurrency;
      }

      // Destination client change
      if (name === 'destinationClient') {
        const subClients = [...Object.keys(clientHierarchy[value] || {})].sort();
        const firstSubClient = subClients[0] || 'DEFAULT';
        const currencies = [...(clientHierarchy[value]?.[firstSubClient] || [])].sort();
        const firstCurrency = currencies[0] || '';

        updated.destinationSubClient = firstSubClient;
        updated.destinationCurrency = firstCurrency;
      }

      // Destination subclient change
      if (name === 'destinationSubClient') {
        const currencies = [...(clientHierarchy[prev.destinationClient]?.[value] || [])].sort();
        const firstCurrency = currencies[0] || '';
        updated.destinationCurrency = firstCurrency;
      }

      return updated;
    });
  };

  return {
    formData,
    handleChange,
    sourceSubClients,
    sourceCurrencies,
    destinationClients,
    destinationSubClients,
    destinationCurrencies
  };
};

